/**
 * Partner-aware Samples Integration API client for the generic /generic/* flow.
 *
 * Every function takes `partner: string` (a registered generic partner id like
 * "caretria") and routes to /generic/{partner}/<endpoint>.
 *
 * IMPORTANT: This client must never be used for Medvantx. Medvantx remains on
 * the legacy `samplesIntegrationApi.ts` (/medvantx/*) which must not be touched.
 */

import axios from "axios";
import type {
  Address,
  FormData,
  Signature,
} from "./samplesIntegrationApi";

const SAMPLES_API_BASE_URL =
  process.env.REACT_APP_SAMPLES_API_URL || "https://integrations-samples-api.dev.impiricus.com";

const samplesApi = axios.create({
  baseURL: SAMPLES_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

/** Build a partner-scoped path under the generic namespace. */
function partnerPath(partner: string, path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `/generic/${partner}${normalized}`;
}

interface SendOTPResponse {
  status: string;
  requestID: string;
  message: string;
  sent: boolean;
  maskedPhone?: string;
}

interface VerifyOTPResponse {
  status: string;
  requestID: string;
  conversationID: string;
  message: string;
  verified: boolean;
}

interface FormDataResponse {
  status: string;
  requestID: string;
  conversationID: string;
  formData: FormData;
}

interface SubmitFormResponse {
  status: string;
  requestID: string;
  conversationID: string;
  message: string;
  submittedAt: string;
}

interface RequestStatusResponse {
  status: string;
  requestID: string;
  conversationID: string;
  otpStatus: string;
  formStatus: string;
  isClosed: boolean;
  available: boolean;
  maskedPhone?: string;
}

export async function sendOTP(partner: string, requestID: string): Promise<SendOTPResponse> {
  try {
    const response = await samplesApi.post<SendOTPResponse>(
      partnerPath(partner, "/send-otp"),
      { requestID }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to send OTP");
    }
    throw new Error("Network error. Please try again.");
  }
}

export async function resendOTP(partner: string, requestID: string): Promise<SendOTPResponse> {
  try {
    const response = await samplesApi.post<SendOTPResponse>(
      partnerPath(partner, "/resend-otp"),
      { requestID }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to resend OTP");
    }
    throw new Error("Network error. Please try again.");
  }
}

export async function verifyOTP(
  partner: string,
  requestID: string,
  otp: string
): Promise<VerifyOTPResponse> {
  try {
    const response = await samplesApi.post<VerifyOTPResponse>(
      partnerPath(partner, "/verify-otp"),
      { requestID, otp }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Invalid OTP");
    }
    throw new Error("Network error. Please try again.");
  }
}

export async function getFormData(partner: string, requestID: string): Promise<FormDataResponse> {
  try {
    const response = await samplesApi.get<FormDataResponse>(
      partnerPath(partner, `/form-data/${requestID}`)
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Form not found or has expired");
    }
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to load form data");
    }
    throw new Error("Network error. Please try again.");
  }
}

export async function submitSampleRequest(
  partner: string,
  requestID: string,
  selectedAddress: Address,
  signature: Signature
): Promise<SubmitFormResponse> {
  const payload = { requestID, selectedAddress, signature };
  console.log(`[generic][${partner}] submitSampleRequest payload:`, JSON.stringify(payload, null, 2));
  try {
    const response = await samplesApi.post<SubmitFormResponse>(
      partnerPath(partner, "/submit-sample-request"),
      payload
    );
    console.log(`[generic][${partner}] submitSampleRequest response:`, JSON.stringify(response.data, null, 2));
    return response.data;
  } catch (error: any) {
    console.error(`[generic][${partner}] submitSampleRequest error:`, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
    });
    if (error.response?.data) {
      throw new Error(
        error.response.data.message ||
          error.response.data.detail ||
          `Server error (${error.response.status})`
      );
    }
    throw new Error("Network error. Please try again.");
  }
}

export async function getRequestStatus(
  partner: string,
  requestID: string
): Promise<RequestStatusResponse> {
  try {
    const response = await samplesApi.get<RequestStatusResponse>(
      partnerPath(partner, `/request-status/${requestID}`)
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Request not found");
    }
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to get request status");
    }
    throw new Error("Network error. Please try again.");
  }
}

/**
 * Helper to extract requestID from URL query parameters.
 * Supports HashRouter format: /#/{partner}/verify-phone?requestID=abc123
 */
export function getRequestIdFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const requestIdParam = urlParams.get("requestID");

  const hashParts = window.location.hash.split("?");
  const hashParams = new URLSearchParams(hashParts[1] || "");
  const hashRequestId = hashParams.get("requestID");

  return requestIdParam || hashRequestId || null;
}

export default {
  sendOTP,
  resendOTP,
  verifyOTP,
  getFormData,
  submitSampleRequest,
  getRequestStatus,
  getRequestIdFromUrl,
};
